/**
 * Ride files and history imports (docs/DATA_SOURCES_PLAN.md, phase U).
 *
 * ``POST /rides/upload`` adds one .fit / .tcx / .gpx ride (free users have a
 * monthly allowance; duplicates don't count). ``POST /rides/import`` takes a
 * ZIP history export (Pro) and imports it in the background; poll
 * ``GET /rides/import/status``. ``POST /rides/import-link`` does the same from
 * the download link of a data-export email (docs/HISTORY_LINK_IMPORT_PLAN.md).
 * A 402 opens the paywall through ``apiFetch``.
 */
import { apiFetch } from './api.ts';

export const RIDE_EXTENSIONS = ['.fit', '.tcx', '.gpx'];
export const ACCEPT = [...RIDE_EXTENSIONS, '.zip'].join(',');

export type UploadOutcome = 'added' | 'duplicate' | 'unreadable' | 'quota' | 'failed';

export interface ImportJob {
  id: number;
  status: 'queued' | 'running' | 'done' | 'failed';
  files_total: number;
  imported: number;
  duplicates: number;
  skipped: number;
  failed: number;
  error: string | null;
}

export type ImportStart =
  | { ok: true; job: ImportJob }
  | { ok: false; reason: 'pro' | 'running' | 'too_large' | 'failed'; job?: ImportJob; maxMb?: number };

export type LinkError = 'invalid_link' | 'link_expired' | 'link_unreachable' | 'not_a_zip' | 'failed';

export type LinkImportStart =
  | { ok: true; job: ImportJob }
  | { ok: false; reason: 'pro' | 'running'; job?: ImportJob }
  | { ok: false; reason: LinkError };

const LINK_ERRORS: ReadonlySet<string> = new Set(['invalid_link', 'link_expired', 'link_unreachable', 'not_a_zip']);

export function isZip(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip');
}

export function isRideFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return RIDE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function form(file: File): FormData {
  const body = new FormData();
  body.append('file', file, file.name);
  return body;
}

export async function uploadRide(file: File): Promise<UploadOutcome> {
  try {
    const resp = await apiFetch('/rides/upload', { method: 'POST', body: form(file) });
    if (resp.status === 201) return 'added';
    if (resp.status === 200) return 'duplicate';
    if (resp.status === 402) return 'quota';
    if (resp.status === 400 || resp.status === 413 || resp.status === 422) return 'unreadable';
    return 'failed';
  } catch {
    return 'failed';
  }
}

export async function startImport(file: File): Promise<ImportStart> {
  try {
    const resp = await apiFetch('/rides/import', { method: 'POST', body: form(file) });
    const body = (await resp.json().catch(() => ({}))) as { job?: ImportJob; max_mb?: number };
    if (resp.status === 202 && body.job) return { ok: true, job: body.job };
    if (resp.status === 402) return { ok: false, reason: 'pro' };
    if (resp.status === 409) return { ok: false, reason: 'running', job: body.job };
    if (resp.status === 413) return { ok: false, reason: 'too_large', maxMb: body.max_mb };
    return { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export async function startLinkImport(url: string): Promise<LinkImportStart> {
  try {
    const resp = await apiFetch('/rides/import-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    const body = (await resp.json().catch(() => ({}))) as { job?: ImportJob; error?: string };
    if (resp.status === 202 && body.job) return { ok: true, job: body.job };
    if (resp.status === 402) return { ok: false, reason: 'pro' };
    if (resp.status === 409) return { ok: false, reason: 'running', job: body.job };
    if (resp.status === 400 && body.error && LINK_ERRORS.has(body.error)) {
      return { ok: false, reason: body.error as LinkError };
    }
    return { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export async function importStatus(): Promise<ImportJob | null> {
  const resp = await apiFetch('/rides/import/status');
  if (!resp.ok) throw new Error(`import status ${resp.status}`);
  const body = (await resp.json()) as { job: ImportJob | null };
  return body.job;
}

export function isFinished(job: ImportJob): boolean {
  return job.status === 'done' || job.status === 'failed';
}
